import React from 'react';
import { Box, Link, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { marked } from 'marked';

/**
 * Renders the assistant's markdown as React elements.
 *
 * The prompt tells the model to answer in markdown with tables and bold numbers, so rendering the
 * reply as plain text showed customers raw pipes and asterisks. Only the lexer is used and every
 * node becomes a component, no HTML string is ever inserted, so a resident name or bill note
 * echoed out of a tool result cannot carry markup into the page.
 */
/** Inline tokens. Anything unrecognised falls back to its own text, never to raw HTML. */
/**
 * Only schemes a link may safely carry. Anything else, javascript: and data: in particular, is
 * dropped rather than rendered: this content comes from a model and from knowledge articles, so it
 * is untrusted by definition.
 */
const SAFE_SCHEMES = ['http:', 'https:', 'mailto:'];
export function safeHref(href) {
  if (!href) return undefined;
  // A relative link stays relative; it cannot carry a scheme.
  if (/^[/#?]/.test(href)) return href;
  try {
    return SAFE_SCHEMES.includes(new URL(href, window.location.origin).protocol) ? href : undefined;
  } catch (_unused) {
    return undefined;
  }
}
function inline() {
  let tokens = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  return tokens.map((token, i) => {
    var _raw;
    switch (token.type) {
      case 'strong':
        return /*#__PURE__*/React.createElement("strong", {
          key: i
        }, inline(token.tokens));
      case 'em':
        return /*#__PURE__*/React.createElement("em", {
          key: i
        }, inline(token.tokens));
      case 'del':
        return /*#__PURE__*/React.createElement("del", {
          key: i
        }, inline(token.tokens));
      case 'codespan':
        return /*#__PURE__*/React.createElement(Box, {
          key: i,
          component: "code",
          sx: {
            px: '4px',
            borderRadius: '4px',
            bgcolor: 'action.hover',
            fontFamily: 'monospace',
            fontSize: '0.92em'
          }
        }, token.text);
      case 'link':
        {
          const t = token;
          const href = safeHref(t.href);
          // A scheme we do not allow is rendered as plain text: escaping the HTML does not make a
          // javascript: URL safe, and clicking model output must never execute script.
          if (!href) return /*#__PURE__*/React.createElement(React.Fragment, {
            key: i
          }, inline(t.tokens));
          // Model output can name any host, so external links never get the opener's window.
          return /*#__PURE__*/React.createElement(Link, {
            key: i,
            href: href,
            target: "_blank",
            rel: "noopener noreferrer"
          }, inline(t.tokens));
        }
      case 'br':
        return /*#__PURE__*/React.createElement("br", {
          key: i
        });
      case 'text':
        {
          var _t$tokens;
          const t = token;
          return (_t$tokens = t.tokens) !== null && _t$tokens !== void 0 && _t$tokens.length ? /*#__PURE__*/React.createElement(React.Fragment, {
            key: i
          }, inline(t.tokens)) : t.text;
        }
      default:
        return (_raw = token.raw) !== null && _raw !== void 0 ? _raw : '';
    }
  });
}
function renderBlock(token, key) {
  var _raw2;
  switch (token.type) {
    case 'heading':
      {
        const t = token;
        return /*#__PURE__*/React.createElement(Typography, {
          key: key,
          component: "h".concat(Math.min(t.depth + 2, 6)),
          variant: t.depth <= 2 ? 'subtitle1' : 'subtitle2',
          sx: {
            fontWeight: 600,
            mt: '14px',
            mb: '6px'
          }
        }, inline(t.tokens));
      }
    case 'paragraph':
      return /*#__PURE__*/React.createElement(Typography, {
        key: key,
        variant: "body2",
        sx: {
          mb: '10px'
        }
      }, inline(token.tokens));
    case 'list':
      {
        const t = token;
        return /*#__PURE__*/React.createElement(Box, {
          key: key,
          component: t.ordered ? 'ol' : 'ul',
          sx: {
            pl: '22px',
            mt: 0,
            mb: '10px',
            '& li': {
              mb: '4px'
            }
          }
        }, t.items.map((item, i) => /*#__PURE__*/React.createElement(Typography, {
          key: i,
          component: "li",
          variant: "body2"
        }, item.tokens.map((child, j) =>
        // A nested list inside a list item is a block; everything else is inline.
        child.type === 'list' || child.type === 'table' ? renderBlock(child, j) : inline([child])))));
      }
    case 'table':
      {
        const t = token;
        return (
          /*#__PURE__*/
          // Wide tables scroll inside their own box so the page never scrolls sideways.
          React.createElement(Box, {
            key: key,
            sx: {
              overflowX: 'auto',
              mb: '14px'
            }
          }, /*#__PURE__*/React.createElement(Table, {
            size: "small",
            sx: {
              minWidth: 'max-content',
              // The app theme sizes cells for data grids; inside an answer that reads as padding.
              '& td, & th': {
                py: '7px',
                px: '12px',
                fontSize: 13.5,
                borderColor: 'divider'
              },
              '& tbody tr:last-of-type td': {
                borderBottom: 0
              },
              '& td': {
                fontVariantNumeric: 'tabular-nums'
              }
            }
          }, /*#__PURE__*/React.createElement(TableHead, null, /*#__PURE__*/React.createElement(TableRow, null, t.header.map((cell, i) => {
            var _t$align$i;
            return /*#__PURE__*/React.createElement(TableCell, {
              key: i,
              align: (_t$align$i = t.align[i]) !== null && _t$align$i !== void 0 ? _t$align$i : 'left',
              sx: {
                fontWeight: 600,
                whiteSpace: 'nowrap'
              }
            }, inline(cell.tokens));
          }))), /*#__PURE__*/React.createElement(TableBody, null, t.rows.map((row, r) => /*#__PURE__*/React.createElement(TableRow, {
            key: r
          }, row.map((cell, c) => {
            var _t$align$c;
            return /*#__PURE__*/React.createElement(TableCell, {
              key: c,
              align: (_t$align$c = t.align[c]) !== null && _t$align$c !== void 0 ? _t$align$c : 'left'
            }, inline(cell.tokens));
          }))))))
        );
      }
    case 'code':
      return /*#__PURE__*/React.createElement(Box, {
        key: key,
        component: "pre",
        sx: {
          overflowX: 'auto',
          p: '10px 12px',
          mb: '12px',
          borderRadius: '6px',
          bgcolor: 'action.hover',
          fontSize: '12.5px',
          fontFamily: 'monospace'
        }
      }, token.text);
    case 'blockquote':
      return /*#__PURE__*/React.createElement(Box, {
        key: key,
        sx: {
          borderLeft: 3,
          borderColor: 'divider',
          pl: '12px',
          mb: '10px'
        }
      }, token.tokens.map(renderBlock));
    case 'hr':
      return /*#__PURE__*/React.createElement(Box, {
        key: key,
        sx: {
          borderTop: 1,
          borderColor: 'divider',
          my: '12px'
        }
      });
    case 'space':
      return null;
    default:
      return /*#__PURE__*/React.createElement(Typography, {
        key: key,
        variant: "body2",
        sx: {
          mb: '10px',
          whiteSpace: 'pre-wrap'
        }
      }, (_raw2 = token.raw) !== null && _raw2 !== void 0 ? _raw2 : '');
  }
}
export function LivyMarkdown(_ref) {
  let content = _ref.content;
  const tokens = React.useMemo(() => marked.lexer(content), [content]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, tokens.map((t, i) => renderBlock(t, i)));
}